import { useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import SnackbarContext from './SnackbarContext';

// Severity types for the snackbar
export const SEVERITY = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Default duration for the snackbar to be shown
const DEFAULT_AUTO_HIDE_DURATION = 5000;

const SnackbarProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState(SEVERITY.INFO);
  const [autoHideDuration, setAutoHideDuration] = useState(DEFAULT_AUTO_HIDE_DURATION);

  const showSnackbar = useCallback((newMessage, newSeverity = SEVERITY.INFO, newDuration = DEFAULT_AUTO_HIDE_DURATION) => {
    setMessage(newMessage);
    setSeverity(newSeverity);
    setAutoHideDuration(newDuration);
    setOpen(true);
  }, []);

  const showError = useCallback((errorMessage, duration = DEFAULT_AUTO_HIDE_DURATION) => {
    showSnackbar(errorMessage, SEVERITY.ERROR, duration);
  }, [showSnackbar]);

  const showSuccess = useCallback((successMessage, duration = DEFAULT_AUTO_HIDE_DURATION) => {
    showSnackbar(successMessage, SEVERITY.SUCCESS, duration);
  }, [showSnackbar]);

  const showWarning = useCallback((warningMessage, duration = DEFAULT_AUTO_HIDE_DURATION) => {
    showSnackbar(warningMessage, SEVERITY.WARNING, duration);
  }, [showSnackbar]);

  const showInfo = useCallback((infoMessage, duration = DEFAULT_AUTO_HIDE_DURATION) => {
    showSnackbar(infoMessage, SEVERITY.INFO, duration);
  }, [showSnackbar]);

  const closeSnackbar = useCallback(() => {
    setOpen(false);
  }, []);

  // Context value to be provided
  const value = {
    showSnackbar,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    closeSnackbar,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;